"use client";

import { ColumnDef } from "@tanstack/react-table";
import { getEventsForSponsors } from "~/app/apply-to-sponsor/actions";
import { Unpacked } from "~/types/helpers";
import Image from "next/image";
import Link from "next/link";
import { Button } from "~/components/v3/themed/Button";
import { getFormattedTimeStamp } from "~/utils/time";
import { RiCalendarEventLine } from "react-icons/ri";
import { MdOutlineLocationOn } from "react-icons/md";
import { cn } from "~/utils/fontsConfig";
import { If } from "~/components/v3/Conditional";
import { SponsorshipPackageTier } from "@prisma/client";
import { useEffect, useState } from "react";

type EventsForSpeakers = Unpacked<
    Awaited<ReturnType<typeof getEventsForSponsors>>
>;

export const columns: ColumnDef<EventsForSpeakers>[] = [
    {
        id: "select",
        header: "",
        size: 40,
        cell: ({ row, table }) => {
            const meta = table.options.meta as {
                selectedIds?: Set<string>;
                onSelectionChange?: (id: string) => void;
            };
            const isSelected = meta?.selectedIds?.has(row.original.id);

            return (
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => meta?.onSelectionChange?.(row.original.id)}
                    className="h-4 w-4 rounded border-gray-300"
                />
            );
        },
    },
    {
        accessorKey: "name",
        header: "Event",
        cell: (cell) => {
            return (
                <div className="flex min-w-[250px] max-w-[450px] flex-col gap-2 md:min-w-[350px]">
                    <Link
                        href={`/events/${cell.row.original.slug}`}
                        target="_blank"
                        className="flex h-max max-w-full break-all text-start"
                    >
                        <div className="flex flex-row items-center">
                            {cell.row.original.image && (
                                <Image
                                    className="hidden rounded-xl object-cover md:block"
                                    src={
                                        cell.row.original.image?.includes(
                                            "http",
                                        )
                                            ? cell.row.original.image
                                            : `${process.env.NEXT_PUBLIC_ASSETS_URL}/${cell.row.original.id}/${cell.row.original.image}`
                                    }
                                    alt={cell.row.original.name}
                                    style={{ width: "auto", height: "auto" }}
                                    width={80}
                                    height={80}
                                />
                            )}
                            <div className="flex min-w-[100px] max-w-[210px] flex-col md:mx-4">
                                <span>{cell.row.original.name}</span>
                                <div className="flex items-center gap-1 text-sm text-v3-N-300">
                                    <RiCalendarEventLine className="hidden h-4  w-4 md:block" />
                                    <span>
                                        {getFormattedTimeStamp(
                                            cell.row.original?.startTime,
                                        )}
                                    </span>
                                </div>
                                <div
                                    className={cn(
                                        "flex items-center gap-1 text-sm text-v3-N-300",
                                        !cell.row.original.city && "hidden",
                                    )}
                                >
                                    <MdOutlineLocationOn className="hidden h-4 w-4 md:block" />
                                    <span>{cell.row.original.city}</span>
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>
            );
        },
    },
    {
        accessorKey: "communityName",
        header: "Community",
        cell: (cell) => {
            return (
                <div className="flex w-full max-w-[200px] flex-col gap-2">
                    <Link
                        href={`/${cell.row.original.communitySlug}`}
                        target="_blank"
                        className="flex h-max max-w-full break-all text-start"
                    >
                        {cell.row.original.communityName}
                    </Link>
                </div>
            );
        },
    },
    {
        accessorKey: "audienceInfo",
        header: "Tags/Audience",
        cell: (cell) => {
            return (
                <div className="flex min-w-[250px] max-w-[380px] flex-wrap gap-2">
                    {cell.row.original.tags?.map((i, key) => {
                        return (
                            <span
                                className="rounded-md bg-[#373a41] px-2 py-1 text-sm"
                                key={key}
                            >
                                {i}
                            </span>
                        );
                    })}
                </div>
            );
        },
    },
    {
        id: "apply",
        header: "",
        cell: ({ row, table }) => {
            const meta = table.options.meta as {
                selectedIds?: Set<string>;
                selectedTier?: SponsorshipPackageTier | null;
                onSelectionChange?: (id: string) => void;
            };

            let isDisabled = !!meta?.selectedTier;

            const buttonText =
                meta?.selectedTier?.durationType === "EVENT_COUNT"
                    ? `Select ${meta.selectedTier.durationValue} events`
                    : "Apply to sponsor";

            return (
                <div className="flex w-full justify-end gap-2">
                    <If condition={row.original.communitySlug === "twist"}>
                        <Link
                            href={`/events/${row.original.slug}/sponsor`}
                            target="_blank"
                        >
                            <Button disabled={isDisabled}>{buttonText}</Button>
                        </Link>
                    </If>
                    <If condition={row.original.communitySlug !== "twist"}>
                        <Link
                            href={`https://2i6voyc6xd3.typeform.com/to/VWiekNjM#community_id=${row.original.communityId}&event_id=${row.original.id}&event_name=${row.original.name}&community_name=${row.original.communityName}&community_slug=${row.original.communitySlug}`}
                            target="_blank"
                        >
                            <Button disabled={isDisabled}>{buttonText}</Button>
                        </Link>
                    </If>
                </div>
            );
        },
    },
];

/**
 * Determines if an event's "Apply to Sponsor" button should be disabled based on:
 * - Selected tier's duration type
 * - Number of events required for the tier
 * - Number of currently selected events
 */
const isEventSponsorshipDisabled = (
    selectedTier: SponsorshipPackageTier | undefined | null,
    selectedEventsCount: number,
    isApplyToSponsorAllowed: boolean = true,
): boolean => {
    // can only sponsor individual events if no tier selected
    // this routes to the event sponsor page / flow
    if (!selectedTier) {
        return false;
    }

    if (!isApplyToSponsorAllowed) {
        return true;
    }

    switch (selectedTier.durationType) {
        case "EVENT_COUNT":
            // For multi-event sponsorships, check if we have selected enough events
            return selectedTier.durationValue
                ? selectedEventsCount < selectedTier.durationValue
                : true;

        case "TIME_PERIOD":
            // For time period sponsorships, allow single event selection
            return false;

        case "CUSTOM":
            // For custom arrangements, require at least one event
            return selectedEventsCount === 0;

        default:
            return true; // Default to disabled if duration type is unknown
    }
};
